import { dayjs, getLocale } from '@/shared/lib/dayjs'
import { RequiredCaption, RequiredMark } from '@/shared/ui/atoms/required-mark'
import type { DatePickerProps as AntDatePickerProps } from 'antd'
import { ConfigProvider, DatePicker } from 'antd'
import type { Dayjs } from 'dayjs'
import { forwardRef, useId, useMemo, type ReactNode } from 'react'
import './datepicker.css'

type AntDatePickerRef = React.ComponentRef<typeof DatePicker>

export interface DatepickerProps {
  label?: string | null
  placeholder?: string
  value?: string | Date | Dayjs | null
  format?: string
  onChange: AntDatePickerProps['onChange']
  locale?: string
  width?: string
  height?: string
  required?: boolean
  errorData?: Record<string, string | null | undefined>
  field?: string
  status?: string
  readOnly?: boolean
  backgroundColor?: string
  disabled?: boolean
  suffix?: ReactNode
  prefix?: ReactNode
  name?: string
  id?: string
  autoComplete?: string
  'aria-label'?: string
  showTime?: boolean | Record<string, any>
  style?: React.CSSProperties
  className?: string
  disabledDate?: (current: Dayjs) => boolean
  /** Pass-through to AntD `DatePicker.picker` — opens a month/quarter/year UI. */
  picker?: AntDatePickerProps['picker']
  /** Pass-through to AntD `DatePicker.allowClear`. */
  allowClear?: AntDatePickerProps['allowClear']
}

export const Datepicker = forwardRef<AntDatePickerRef, DatepickerProps>(function Datepicker(
  {
    label,
    placeholder,
    value,
    format = 'DD/MM/YYYY',
    onChange,
    locale = 'uzCYR',
    width = '100%',
    height = '40px',
    required,
    errorData,
    field,
    status,
    readOnly,
    disabled,
    id: idProp,
    name,
    autoComplete,
    'aria-label': ariaLabel,
    ...rest
  },
  ref,
) {
  getLocale(locale)
  const fallbackId = useId()
  const id = idProp ?? fallbackId

  const parsedValue = useMemo(() => {
    if (!value) return null
    if (dayjs.isDayjs(value)) return (value as Dayjs).isValid() ? (value as Dayjs) : null
    const withFormat = dayjs(value as string, format, true)
    if (withFormat.isValid()) return withFormat
    const native = dayjs(value)
    return native.isValid() ? native : null
  }, [value, format])

  return (
    <ConfigProvider
      theme={{
        components: {
          DatePicker: {
            zIndexPopup: 9999,
          },
        },
      }}
    >
      <div className='flex flex-col gap-2 relative w-full w-(width)'>
        {label && (
          <label htmlFor={id} className='text-xs font-medium text-(--form-header-title)'>
            {label}
            {required && <RequiredMark />}
          </label>
        )}
        <DatePicker
          ref={ref}
          id={id}
          name={name ?? id}
          autoComplete={autoComplete}
          aria-label={ariaLabel ?? label ?? undefined}
          className='glasspro-datepicker'
          allowClear
          styles={{ root: { width, height } }}
          format={format}
          placeholder={placeholder}
          value={parsedValue}
          onChange={onChange}
          disabled={disabled || readOnly}
          {...rest}
        />
        {status === 'error' && errorData && (
          <RequiredCaption errorData={errorData} field={field || label || ''} />
        )}
      </div>
    </ConfigProvider>
  )
})
